import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useRouter } from "next/router";
import {
	brazeContentCardsManager,
	type BrazeContentCard,
} from "../utils/brazeContentCards";

interface UseBrazeContentCardsReturn {
	cards: BrazeContentCard[];
	loading: boolean;
	error: string | null;
	unviewedCount: number;
	refreshCards: () => void;
	forceInitialize: () => void;
	isBrazeAvailable: boolean;
	logCardClick: (cardId: string) => void;
	logCardDismissal: (cardId: string) => void;
	logCardImpression: (cardId: string) => void;
	getCardsByType: (contentCardType: string) => BrazeContentCard[];
	getCardsForPage: (pageName: string) => BrazeContentCard[];
	getCardsWithPromoCode: () => BrazeContentCard[];
	getCardsWithFirstName: () => BrazeContentCard[];
	examineCardKeys: () => {
		content_card_type: string[];
		page_names: string[];
		promo_code: string[];
		first_name: string[];
	};
	getCardsWithSpecificKeys: () => Array<{
		card: BrazeContentCard;
		content_card_type?: string;
		page_names?: string;
		promo_code?: string;
		first_name?: string;
	}>;
}

export const useBrazeContentCards = (): UseBrazeContentCardsReturn => {
	const [cards, setCards] = useState<BrazeContentCard[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const router = useRouter();
	const unsubscribeRef = useRef<(() => void) | null>(null);
	const isInitializedRef = useRef(false);

	// useLayoutEffect runs synchronously before the browser paints.
	// This resolves which content to show (Braze or Contentful) before
	// the user sees anything — no blur, no flash, no content swap.
	useLayoutEffect(() => {
		if (isInitializedRef.current) return;

		// If Braze SDK isn't available (e.g. blocked by ad blocker),
		// resolve immediately — show Contentful on the very first paint.
		if (typeof window === "undefined" || !window.braze) {
			setLoading(false);
			return;
		}

		try {
			// Unsubscribe from any existing subscription before creating a new one
			if (unsubscribeRef.current) {
				unsubscribeRef.current();
				unsubscribeRef.current = null;
			}

			// Force setup so cached cards are available for the synchronous check
			brazeContentCardsManager.forceInitialize();

			unsubscribeRef.current = brazeContentCardsManager.subscribe(
				(updatedCards) => {
					const uniqueCards = updatedCards.filter(
						(card, index, self) =>
							index === self.findIndex((c) => c.id === card.id),
					);

					setCards(uniqueCards);
					setLoading(false);
					setError(null);
				},
			);

			// subscribe() fires its callback synchronously with cached cards.
			// If Braze had cached cards, loading is already false.
			// If not, stop loading anyway — Contentful shows immediately and
			// if fresh Braze cards arrive later the subscription updates reactively.
			setLoading(false);

			isInitializedRef.current = true;
		} catch (err) {
			console.error("Error subscribing to Content Cards:", err);
			setError(`Error subscribing to Content Cards: ${err}`);
			setLoading(false);
		}

		return () => {
			if (unsubscribeRef.current) {
				unsubscribeRef.current();
				unsubscribeRef.current = null;
				isInitializedRef.current = false;
			}
		};
	}, []);

	// Refresh cards when route changes
	useEffect(() => {
		if (!router.isReady) return;

		// Refresh cards when route changes to ensure we get updated cards for the new page
		if (
			isInitializedRef.current &&
			brazeContentCardsManager.isBrazeAvailable()
		) {
			brazeContentCardsManager.refreshCards();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.asPath, router.isReady]);

	const refreshCards = () => {
		brazeContentCardsManager.refreshCards();
	};

	const forceInitialize = () => {
		brazeContentCardsManager.forceInitialize();
	};

	const isBrazeAvailable = brazeContentCardsManager.isBrazeAvailable();

	const logCardClick = (cardId: string) => {
		brazeContentCardsManager.logCardClick(cardId);
	};

	const logCardDismissal = (cardId: string) => {
		brazeContentCardsManager.logCardDismissal(cardId);
	};

	const logCardImpression = (cardId: string) => {
		brazeContentCardsManager.logCardImpression(cardId);
	};

	const getCardsByType = (contentCardType: string) => {
		return brazeContentCardsManager.getCardsByType(contentCardType);
	};

	const getCardsForPage = (pageName: string) => {
		return brazeContentCardsManager.getCardsForPage(pageName);
	};

	const getCardsWithPromoCode = () => {
		return brazeContentCardsManager.getCardsWithPromoCode();
	};

	const getCardsWithFirstName = () => {
		return brazeContentCardsManager.getCardsWithFirstName();
	};

	const examineCardKeys = () => {
		return brazeContentCardsManager.examineCardKeys();
	};

	const getCardsWithSpecificKeys = () => {
		return brazeContentCardsManager.getCardsWithSpecificKeys();
	};

	const unviewedCount = brazeContentCardsManager.getUnviewedCount();

	return {
		cards,
		loading,
		error,
		unviewedCount,
		refreshCards,
		forceInitialize,
		isBrazeAvailable,
		logCardClick,
		logCardDismissal,
		logCardImpression,
		getCardsByType,
		getCardsForPage,
		getCardsWithPromoCode,
		getCardsWithFirstName,
		examineCardKeys,
		getCardsWithSpecificKeys,
	};
};
